import ProgramsApi from '../api/programs';

export default {
  namespaced: true,
  state: {
    programs: [],
    selectedProgram: null,
    // 'idle' | 'loading' | 'ready' | 'error'
    programsStatus: 'idle',
  },
  getters: {
    loadingPrograms(state) {
      return state.programsStatus === 'loading';
    },
    programsStatus(state) {
      return state.programsStatus;
    },
    programsReady(state) {
      return state.programsStatus === 'ready';
    },
    programsFailed(state) {
      return state.programsStatus === 'error';
    },
    allPrograms(state) {
      return state.programs;
    },
    selectedProgram(state) {
      return state.selectedProgram;
    },
  },
  actions: {
    getPrograms(context) {
      context.commit('SET_PROGRAMS_STATUS', 'loading');

      return new Promise((resolve, reject) => {
        ProgramsApi.getPrograms()
          .then((response) => {
            context.commit('SET_PROGRAMS', response.result);

            const currentUser = context.rootGetters['users/currentUser'];
            if (response.result.length > 0) {
              const foundProgram = response.result.find(
                program => program._id === currentUser?.selected_program?._id,
              );

              context.commit('SET_SELECTED_PROGRAM', foundProgram || response.result[0]);
            }

            context.commit('SET_PROGRAMS_STATUS', 'ready');
            resolve(response);
          })
          .catch((error) => {
            context.commit('SET_PROGRAMS_STATUS', 'error');
            reject(error);
          });
      });
    },
    /**
     * Loads program context for the app gate. Retries once, then leaves the
     * status as 'error'. Never rejects: the status carries the outcome.
     */
    async loadPrograms(context) {
      try {
        await context.dispatch('getPrograms');
        return true;
      } catch (firstError) {
        console.error('Failed to load programs, retrying:', firstError);
      }

      try {
        await context.dispatch('getPrograms');
        return true;
      } catch (retryError) {
        console.error('Failed to load programs after retry:', retryError);
        return false;
      }
    },
    getSelectedProgram(context, { programSlug, populate = false }) {
      return new Promise((resolve, reject) => {
        ProgramsApi.getProgram(programSlug, populate)
          .then((response) => {
            context.commit('SET_SELECTED_PROGRAM', response);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    createProgram(context, program) {
      return new Promise((resolve, reject) => {
        ProgramsApi.createProgram(program)
          .then((response) => {
            context.commit('ADD_PROGRAM', response.data);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    updateProgram(context, program) {
      return new Promise((resolve, reject) => {
        ProgramsApi.updateProgram(program)
          .then((response) => {
            context.commit('SET_SELECTED_PROGRAM', response.data);

            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    archiveProgram(context, program) {
      return new Promise((resolve, reject) => {
        ProgramsApi.updateProgram({
          ...program,
          // midnight of the current day
          endDate: new Date(new Date().setHours(0, 0, 0, 0)),
        })
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    inviteUsers(context, payload) {
      return new Promise((resolve, reject) => {
        ProgramsApi.inviteUsers(payload)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getInviteLink(context, { userEmail, programSlug }) {
      return new Promise((resolve, reject) => {
        ProgramsApi.generateInviteLink(userEmail, programSlug)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    reSendInvite(context, { userEmail, programSlug }) {
      return new Promise((resolve, reject) => {
        ProgramsApi.reSendInvite(userEmail, programSlug)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    notifyProgramMember(context, payload) {
      return new Promise((resolve, reject) => {
        ProgramsApi.notifyProgramMember(payload)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    generateProgramReport(_context, programSlug) {
      return ProgramsApi.generateProgramReport(programSlug)
        .then(response => response)
        .catch((error) => {
          console.error('Error generating report:', error);
          throw error;
        });
    },
  },
  mutations: {
    ADD_PROGRAM: (state, program) => {
      state.programs = [program, ...state.programs];
    },
    SET_PROGRAMS: (state, programs) => {
      state.programs = programs;
    },
    SET_PROGRAMS_STATUS: (state, status) => {
      state.programsStatus = status;
    },
    SET_SELECTED_PROGRAM: (state, program) => {
      state.selectedProgram = program;

      if (program) {
        const index = state.programs.findIndex(p => p._id === program._id);
        if (index !== -1) {
          state.programs.splice(index, 1, program);
        }
      }
    },
  },
};
